/**
 * StatusBadge - Status indicator with color mapping
 * 
 * Features:
 * - Predefined status colors
 * - Custom status support
 * - Size variants
 * - Icon support
 */

import React from 'react';

const STATUS_COLORS = {
  // User statuses
  active: 'success',
  inactive: 'secondary',
  confirmed: 'success',
  unconfirmed: 'warning',
  disabled: 'error',
  
  // Subscription statuses
  trial: 'info',
  cancelled: 'error',
  expired: 'secondary',
  
  // Payment statuses
  succeeded: 'success',
  pending: 'warning',
  failed: 'error',
  refunded: 'secondary',
  
  // Ticket statuses
  open: 'info',
  'in-progress': 'warning',
  resolved: 'success',
  closed: 'secondary',
  
  // System statuses
  healthy: 'success',
  degraded: 'warning',
  down: 'error',
  
  // Generic statuses
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  secondary: 'secondary',
};

const STATUS_ICONS = {
  success: '✓',
  warning: '⚠',
  error: '✕',
  info: 'ℹ',
  secondary: '○',
};

const StatusBadge = ({
  status,
  label,
  text,
  variant,
  size = 'medium', // 'small', 'medium', 'large'
  showIcon = false,
  className = ''
}) => {
  const displayLabel = label || text || status;
  const statusKey = status || text;
  const badgeVariant = variant || STATUS_COLORS[statusKey?.toLowerCase()] || 'secondary';
  const icon = showIcon ? STATUS_ICONS[badgeVariant] : null;

  return (
    <span 
      className={`admin-status-badge admin-status-badge--${badgeVariant} admin-status-badge--${size} ${className}`}
      role="status"
      aria-label={`Status: ${displayLabel}`}
    >
      {icon && <span className="admin-status-badge__icon">{icon}</span>}
      <span className="admin-status-badge__label">{displayLabel}</span>
    </span>
  );
};

export const StatusDot = ({ status, variant, size = 'medium', className = '' }) => {
  const dotVariant = variant || STATUS_COLORS[status?.toLowerCase()] || 'secondary';

  return (
    <span 
      className={`admin-status-dot admin-status-dot--${dotVariant} admin-status-dot--${size} ${className}`}
      role="status"
      aria-label={`Status: ${status}`}
      title={status}
    />
  );
};

export const StatusLabel = ({ status, label, variant, className = '' }) => {
  const displayLabel = label || status;
  const labelVariant = variant || STATUS_COLORS[status?.toLowerCase()] || 'secondary';

  return (
    <div className={`admin-status-label ${className}`}>
      <StatusDot status={status} variant={labelVariant} />
      <span className="admin-status-label__text">{displayLabel}</span>
    </div>
  );
};

export default StatusBadge;
